import { FC, useEffect, useState } from "react";
import classes from './idmFeaturesWidget.module.scss'
import { idmService, IIdmFeature, IIdmRole } from "../../../../entities/idm";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { SearchItems } from "../../../../features/searchItems";
import { useMyActions } from "../../../../entities/my";
import { Dropdown } from "../../../../shared/ui/dropdown";
import { FEATURE_SECTIONS } from "../../lib/featureSections";

export const IdmFeaturesWidget: FC = () => {

    const [features, setFeatures] = useState<IIdmFeature[]>([])
    const [featureSearchItems, setFeatureSearchItems] = useState<Array<IIdmFeature & {name: string}>>([])
    const [roles, setRoles] = useState<IIdmRole[]>([])
    const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
    const [selectedRoleFeatureIds, setSelectedRoleFeatureIds] = useState<number[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isRoleFilterLoading, setIsRoleFilterLoading] = useState<boolean>(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const getFeatureSearchName = (feature: IIdmFeature) => `${feature.name} ${feature.key} ${feature.description} ${feature.category}`
    const getSortedFeatures = (items: IIdmFeature[]) => [...items].sort((a, b) => a.name.localeCompare(b.name))

    useEffect(() => {
        const loadFeatures = async () => {
            try{
                setIsLoading(true)
                const [featuresRes, rolesRes] = await Promise.all([
                    idmService.listFeatures(),
                    idmService.listRoles()
                ])
                const sortedFeatures = getSortedFeatures(featuresRes.features)
                const sortedRoles = rolesRes.sort((a, b) => a.hierarchy_level - b.hierarchy_level || a.name.localeCompare(b.name))
                setFeatures(sortedFeatures)
                setRoles(sortedRoles)
                setFeatureSearchItems(sortedFeatures.map(feature => ({...feature, name: getFeatureSearchName(feature)})))
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении справочника фич', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }

        loadFeatures()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSelectRole = async (roleId: number | null) => {
        setSelectedRoleId(roleId)

        if(roleId === null){
            setSelectedRoleFeatureIds([])
            setFeatureSearchItems(features.map(feature => ({...feature, name: getFeatureSearchName(feature)})))
            return
        }

        try{
            setIsRoleFilterLoading(true)
            const roleFeaturesRes = await idmService.listRoleFeatures(roleId)
            const roleFeatureIds = roleFeaturesRes.features.map(feature => feature.id)
            setSelectedRoleFeatureIds(roleFeatureIds)
            const filteredFeatures = features.filter(feature => roleFeatureIds.includes(feature.id))
            setFeatureSearchItems(filteredFeatures.map(feature => ({...feature, name: getFeatureSearchName(feature)})))
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении фич роли', type: 'error'})
            }
        }
        finally{
            setIsRoleFilterLoading(false)
        }
    }

    const selectedRole = roles.find(role => role.id === selectedRoleId) || null
    const groupedFeatures = (() => {
        const featureMap = new Map(featureSearchItems.map(feature => [feature.key, feature]))
        const usedKeys = new Set<string>()

        const sections = FEATURE_SECTIONS.map(section => {
            const items = section.keys
                .map(key => {
                    const feature = featureMap.get(key)
                    if(feature){
                        usedKeys.add(key)
                    }
                    return feature || null
                })
                .filter((feature): feature is IIdmFeature & {name: string} => Boolean(feature))

            return {title: section.title, items}
        }).filter(section => section.items.length > 0)

        const rest = featureSearchItems
            .filter(feature => !usedKeys.has(feature.key))
            .sort((a, b) => a.name.localeCompare(b.name))

        if(rest.length > 0){
            sections.push({title: 'Прочее', items: rest})
        }

        return sections
    })()

    return (
        <section className={classes.container}>
            <section className={classes.filters}>
                <section className={classes.search}>
                    <SearchItems
                        placeholder="Поиск по названию, key, категории или описанию фичи"
                        items={(selectedRoleId === null ? features : features.filter(feature => selectedRoleFeatureIds.includes(feature.id))).map(feature => ({
                            ...feature,
                            name: getFeatureSearchName(feature)
                        }))}
                        setItems={setFeatureSearchItems}
                    />
                </section>
                <section className={classes.roleFilter}>
                    <Dropdown
                        title={<span className={classes.roleFilterTitle}>Роль: {selectedRole ? selectedRole.name : 'Все роли'}</span>}
                    >
                        <section className={classes.roleOptions}>
                            <section
                                className={classes.roleOption + (selectedRoleId === null ? ` ${classes.roleOptionActive}` : '')}
                                onClick={() => onSelectRole(null)}
                            >
                                Все роли
                            </section>
                            {roles.map(role => (
                                <section
                                    key={role.id}
                                    className={classes.roleOption + (selectedRoleId === role.id ? ` ${classes.roleOptionActive}` : '')}
                                    onClick={() => onSelectRole(role.id)}
                                >
                                    {role.name}
                                </section>
                            ))}
                        </section>
                    </Dropdown>
                </section>
            </section>
            {
                isLoading || isRoleFilterLoading
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                <section className={classes.list}>
                    {
                        groupedFeatures.length
                            ?
                        groupedFeatures.map(section => (
                            <section key={section.title} className={classes.section}>
                                <section className={classes.sectionTitle}>{section.title}</section>
                                <section className={classes.sectionList}>
                                    {section.items.map(feature => (
                                        <section className={classes.card} key={feature.id || feature.key}>
                                            <section className={classes.head}>
                                                <section className={classes.name}>{feature.name}</section>
                                                <section className={classes.meta}>
                                                    <span className={classes.badge}>{feature.category || 'Без категории'}</span>
                                                    <span className={classes.badge}>ID: {feature.id}</span>
                                                </section>
                                            </section>
                                            <section className={classes.key}>{feature.key}</section>
                                            <section className={classes.description}>
                                                {feature.description || 'Описание фичи не указано'}
                                            </section>
                                        </section>
                                    ))}
                                </section>
                            </section>
                        ))
                            :
                        <section className={classes.empty}>Фичи не найдены</section>
                    }
                </section>
            }
        </section>
    )
}
